import { useMemo, useState, type ReactNode } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputAdornment,
  Popover,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArrowDownward as ExportIcon,
  FilterList as FilterListIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Settings as ViewOptionsIcon,
  ViewColumn as ViewColumnIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { resolveTKey } from '../../../i18n/resolve-key';
import type { ActionDescriptor, TableDescriptor } from '@finance/contracts';
import type { ActionExecutionContext } from '../../../runtime/actions/action-executor.ts';
import type { TableState } from './table-state.js';
import { TableGlobalSearch } from './search/TableGlobalSearch.tsx';
import { resolveActions, type ResolvedAction } from '../../../runtime/actions/action-resolution';
import { useEvaluationContext } from '../../../runtime/conditions/use-evaluation-context';
import { ActionRenderer } from '../ActionRenderer.tsx';
import { getOrderedExportableColumns, getVisibleExportableColumns } from './table-export-policy.js';
import { ExportExperience } from '../../transfer/export/ExportExperience';

interface ExportOptions {
  scope: 'page' | 'selection' | 'all';
  format: 'csv' | 'xlsx';
  fields?: string[];
}
interface TableToolbarProps {
  descriptor: TableDescriptor;
  state: TableState;
  totalCount: number;
  selectedCount: number;
  loading: boolean;
  onRefresh: () => void;
  onFilterRowToggle: () => void;
  onClearFilters: () => void;
  onColumnVisibilityChange: (columnId: string, visible: boolean) => void;
  onDensityChange: (density: 'compact' | 'standard' | 'comfortable') => void;
  onGroupingChange: (grouping: TableState['grouping']) => void;
  onAggregationsChange: (aggregations: TableState['aggregations']) => void;
  onCreateAction: (action: ResolvedAction, context?: ActionExecutionContext) => void;
  onPageAction: (action: ResolvedAction, context?: ActionExecutionContext) => void;
  onBulkAction: (
    action: ResolvedAction,
    selectedIds: string[],
    context?: ActionExecutionContext
  ) => void;
  onSavedViewSelect: (viewId: string) => void;
  onSavedViewSave: (name: string) => void;
  onSavedViewDelete: (viewId: string) => void;
  onResetLayout: () => void;
  onResetAll: () => void;
  showFilterRow: boolean;
  availableViews: { id: string; name: string; isDefault: boolean; isShared: boolean }[];
  currentViewId: string | null;
  unsavedChanges: boolean;
  showGlobalSearch: boolean;
  onGlobalSearchChange: (search: string) => void;
  bulkActions: ActionDescriptor[];
  pageActions: ActionDescriptor[];
  createAction: ActionDescriptor | null;
  exportEnabled: boolean;
  onExport?: (options: ExportOptions) => void;
  /** @deprecated Use descriptor-driven `pageActions` instead. */
  actionNode?: ReactNode;
}

function readableFilterValue(value: unknown): string {
  if (Array.isArray(value)) return value.map(readableFilterValue).join(' – ');
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return String(record.label ?? record.name ?? record.id ?? '');
  }
  return value == null ? '' : String(value);
}

export function TableToolbar({
  descriptor,
  state,
  totalCount,
  selectedCount,
  loading,
  onRefresh,
  onFilterRowToggle,
  onClearFilters,
  onColumnVisibilityChange,
  onDensityChange,
  onGroupingChange,
  onAggregationsChange,
  onCreateAction,
  onPageAction,
  onBulkAction,
  onSavedViewSelect,
  onSavedViewSave,
  onSavedViewDelete,
  onResetLayout,
  onResetAll,
  showFilterRow,
  availableViews,
  currentViewId,
  unsavedChanges,
  showGlobalSearch,
  onGlobalSearchChange,
  bulkActions,
  pageActions,
  createAction,
  exportEnabled,
  onExport,
  actionNode,
}: TableToolbarProps) {
  const { t } = useTranslation();
  const evalCtx = useEvaluationContext();
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('md'));
  const isNarrow = useMediaQuery(theme.breakpoints.down('sm'));
  const [bulkAnchorEl, setBulkAnchorEl] = useState<HTMLElement | null>(null);
  const [pageOverflowAnchorEl, setPageOverflowAnchorEl] = useState<HTMLElement | null>(null);
  const [viewOptionsOpen, setViewOptionsOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportScope, setExportScope] = useState<'page' | 'selection' | 'all'>('page');
  const [exportFields, setExportFields] = useState<string[]>([]);
  const [saveViewName, setSaveViewName] = useState('');

  const capabilities = descriptor.capabilities ?? {};
  const toolbar = descriptor.toolbar ?? {};
  const hasActiveFilters = state.filters.length > 0 || Boolean(state.globalSearch);
  const hasSelection = selectedCount > 0;
  const canShowGlobalSearch = toolbar.showGlobalSearch ?? showGlobalSearch;
  const canShowFilterRowToggle = toolbar.showFilterRowToggle ?? capabilities.filtering === true;
  const canShowActiveFilterSummary = toolbar.showActiveFilterSummary ?? true;
  const canShowClearFilters = toolbar.showClearFilters ?? true;
  const canShowColumnChooser = toolbar.showColumnChooser ?? capabilities.columnVisibility === true;
  const canShowDensity = toolbar.showDensity ?? capabilities.density === true;
  const canShowGrouping = toolbar.showGrouping ?? capabilities.grouping === true;
  const canShowRefresh = toolbar.showRefresh ?? capabilities.refresh === true;
  const canShowExport = toolbar.showExport ?? capabilities.export === true;
  const canShowCreateAction = toolbar.showCreateAction ?? capabilities.create === true;
  const canShowPageActions = toolbar.showPageActions ?? true;
  const canShowBulkActions = toolbar.showBulkActions ?? capabilities.selection === true;
  const canShowSavedViews = toolbar.showSavedViews ?? capabilities.savedViews === true;
  const canShowResetLayout = toolbar.showResetLayout ?? true;
  const canShowResetAll = toolbar.showResetTableState ?? true;
  const showResultCount = toolbar.showResultCount ?? true;
  const showSelectedCount = toolbar.showSelectedCount ?? capabilities.selection === true;
  const exportScopes = descriptor.export?.scopes ?? ['page', 'selection'];

  const resolvedBulkActions = useMemo(
    () => resolveActions(bulkActions, { ctx: evalCtx }),
    [bulkActions, evalCtx]
  );
  const resolvedPageActions = useMemo(
    () => (canShowPageActions ? resolveActions(pageActions, { ctx: evalCtx }) : []),
    [canShowPageActions, evalCtx, pageActions]
  );
  const resolvedCreateAction = useMemo(() => {
    if (!createAction || !canShowCreateAction) return null;
    return resolveActions([createAction], { ctx: evalCtx })[0] ?? null;
  }, [canShowCreateAction, createAction, evalCtx]);

  const sortedPageActions = useMemo(() => {
    const groupPriority: Record<string, number> = {
      primary: 0,
      secondary: 1,
      tertiary: 2,
      danger: 3,
      navigation: 4,
    };
    return [...resolvedPageActions].sort((left, right) => {
      const groupDifference =
        (groupPriority[left.group] ?? 99) - (groupPriority[right.group] ?? 99);
      return groupDifference || left.order - right.order;
    });
  }, [resolvedPageActions]);

  const { visiblePageActions, overflowPageActions } = useMemo(() => {
    const visibleGroups = new Set(['primary', 'secondary', 'tertiary']);
    if (isNarrow) visibleGroups.delete('secondary');
    if (isNarrow || isCompact) visibleGroups.delete('tertiary');
    return {
      visiblePageActions: sortedPageActions.filter(action => visibleGroups.has(action.group)),
      overflowPageActions: sortedPageActions.filter(action => !visibleGroups.has(action.group)),
    };
  }, [isCompact, isNarrow, sortedPageActions]);

  const requiredColumns = descriptor.columns.filter(column => column.requiredVisible);
  const optionalColumns = descriptor.columns.filter(
    column => !column.requiredVisible && column.hideable !== false
  );
  const allExportableColumns = useMemo(
    () => getOrderedExportableColumns(descriptor, state),
    [descriptor, state]
  );
  const visibleExportableColumns = useMemo(
    () => getVisibleExportableColumns(descriptor, state),
    [descriptor, state]
  );
  const allExportableFields = useMemo(
    () => allExportableColumns.map(column => column.field),
    [allExportableColumns]
  );
  const visibleExportableFields = useMemo(
    () => visibleExportableColumns.map(column => column.field),
    [visibleExportableColumns]
  );
  const allFieldsSelected =
    allExportableFields.length > 0 &&
    allExportableFields.every(field => exportFields.includes(field));

  const toggleExportField = (field: string, checked: boolean) => {
    setExportFields(current =>
      checked
        ? current.includes(field)
          ? current
          : [...current, field]
        : current.filter(candidate => candidate !== field)
    );
  };

  const openExportDialog = () => {
    const preferredScope =
      hasSelection && exportScopes.includes('selection')
        ? 'selection'
        : descriptor.export?.defaultScope;
    const firstAvailable =
      exportScopes.find(scope => scope !== 'selection' || hasSelection) ?? 'page';
    setExportScope(
      preferredScope &&
        exportScopes.includes(preferredScope) &&
        (preferredScope !== 'selection' || hasSelection)
        ? preferredScope
        : firstAvailable
    );
    setExportFields(
      visibleExportableFields.length > 0 ? [...visibleExportableFields] : [...allExportableFields]
    );
    setExportDialogOpen(true);
  };

  const saveView = () => {
    const name = saveViewName.trim();
    if (!name) return;
    onSavedViewSave(name);
    setSaveViewName('');
  };

  const activeFilterChips = useMemo(
    () =>
      state.filters.map(filter => {
        const column = descriptor.columns.find(candidate => candidate.field === filter.field);
        const columnLabel = column ? t(resolveTKey(column.labelKey)) : filter.field;
        const operatorLabel = t(resolveTKey(`filters.operators.${filter.operator}`));
        const value = readableFilterValue(filter.value);
        return {
          key: `${filter.field}:${filter.operator}:${value}`,
          label: value
            ? `${columnLabel} · ${operatorLabel}: ${value}`
            : `${columnLabel} · ${operatorLabel}`,
        };
      }),
    [descriptor.columns, state.filters, t]
  );

  const hasViewOptions =
    canShowColumnChooser ||
    canShowDensity ||
    canShowGrouping ||
    canShowSavedViews ||
    canShowRefresh ||
    canShowResetLayout ||
    canShowResetAll;

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 1.25,
        p: { xs: 1.25, sm: 1.5 },
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        minWidth: 0,
      }}
    >
      {(toolbar.titleKey || toolbar.descriptionKey) && (
        <Box sx={{ minWidth: 0 }}>
          {toolbar.titleKey && (
            <Typography variant="subtitle1" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
              {t(resolveTKey(toolbar.titleKey))}
            </Typography>
          )}
          {toolbar.descriptionKey && (
            <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
              {t(resolveTKey(toolbar.descriptionKey))}
            </Typography>
          )}
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
            flex: '1 1 360px',
            minWidth: 0,
          }}
        >
          {hasSelection && showSelectedCount ? (
            <Chip
              variant="outlined"
              label={`${selectedCount} ${t(resolveTKey('table.selected'))}`}
              sx={{ fontWeight: 600 }}
            />
          ) : showResultCount ? (
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              {t(resolveTKey('table.resultsCount'), { count: totalCount })}
            </Typography>
          ) : null}

          {canShowGlobalSearch && (
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 260px' }, minWidth: 0, maxWidth: 440 }}>
              <TableGlobalSearch
                value={state.globalSearch}
                onChange={onGlobalSearchChange}
                placeholder={t(resolveTKey('table.globalSearch.placeholder'))}
                disabled={loading}
              />
            </Box>
          )}

          {canShowFilterRowToggle && descriptor.columns.some(column => column.filterable) && (
            <Tooltip
              title={t(resolveTKey(showFilterRow ? 'table.hideFilterRow' : 'table.showFilterRow'))}
            >
              <Button
                data-testid="table-filter-row-toggle"
                size="small"
                startIcon={<FilterListIcon fontSize="small" />}
                onClick={onFilterRowToggle}
                disabled={loading}
                variant={showFilterRow ? 'contained' : 'outlined'}
                color={showFilterRow ? 'primary' : 'inherit'}
              >
                {isNarrow
                  ? ''
                  : t(resolveTKey(showFilterRow ? 'table.hideFilterRow' : 'table.showFilterRow'))}
              </Button>
            </Tooltip>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: { xs: 'stretch', sm: 'flex-end' },
            gap: isCompact ? 0.5 : 1,
            flexWrap: 'wrap',
            flex: { xs: '1 1 100%', sm: '0 1 auto' },
            minWidth: 0,
            '& > *': { maxWidth: '100%' },
          }}
        >
          {hasSelection && canShowBulkActions && resolvedBulkActions.length > 0 && (
            <>
              <Button
                size="small"
                startIcon={<MoreVertIcon fontSize="small" />}
                onClick={event => setBulkAnchorEl(event.currentTarget)}
                disabled={loading}
                variant="outlined"
                aria-haspopup="dialog"
                aria-expanded={Boolean(bulkAnchorEl)}
              >
                {isNarrow
                  ? selectedCount
                  : `${t(resolveTKey('table.bulkActions'))} (${selectedCount})`}
              </Button>
              <Popover
                anchorEl={bulkAnchorEl}
                open={Boolean(bulkAnchorEl)}
                onClose={() => setBulkAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { p: 1, minWidth: 240, maxWidth: 360 } } }}
              >
                <Box
                  role="dialog"
                  aria-label={t(resolveTKey('table.bulkActions'))}
                  sx={{ display: 'grid', gap: 0.75 }}
                >
                  {resolvedBulkActions.map(action => (
                    <ActionRenderer
                      key={action.action.id}
                      action={action}
                      size="small"
                      fullWidth
                      context={{ source: 'table-bulk', selectedIds: state.selection.selectedIds }}
                      onAction={(next, context) => {
                        setBulkAnchorEl(null);
                        onBulkAction(next, state.selection.selectedIds, context);
                      }}
                    />
                  ))}
                </Box>
              </Popover>
            </>
          )}

          {!hasSelection &&
            visiblePageActions.map(action => (
              <ActionRenderer
                key={action.action.id}
                action={{ ...action, disabled: loading || action.disabled }}
                context={{ source: 'page' }}
                onAction={onPageAction}
                size="small"
              />
            ))}

          {canShowExport && exportEnabled && onExport && exportScopes.length > 0 && (
            <Button
              size="small"
              startIcon={<ExportIcon fontSize="small" />}
              onClick={openExportDialog}
              disabled={loading || allExportableColumns.length === 0}
              variant="outlined"
              aria-label={t(resolveTKey('table.exportLabel'))}
              data-testid="table-export-button"
            >
              {isNarrow ? '' : t(resolveTKey('table.exportLabel'))}
            </Button>
          )}

          {!hasSelection && resolvedCreateAction && (
            <ActionRenderer
              action={{
                ...resolvedCreateAction,
                disabled: loading || resolvedCreateAction.disabled,
                action: {
                  ...resolvedCreateAction.action,
                  iconKey: resolvedCreateAction.action.iconKey ?? ('add' as never),
                  intent: 'primary',
                },
              }}
              context={{ source: 'table-create' }}
              onAction={onCreateAction}
              size="small"
            />
          )}

          {!hasSelection && overflowPageActions.length > 0 && (
            <>
              <Tooltip title={t(resolveTKey('table.moreActions'))}>
                <IconButton
                  size="small"
                  onClick={event => setPageOverflowAnchorEl(event.currentTarget)}
                  aria-label={t(resolveTKey('table.moreActions'))}
                  aria-haspopup="dialog"
                  aria-expanded={Boolean(pageOverflowAnchorEl)}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Popover
                anchorEl={pageOverflowAnchorEl}
                open={Boolean(pageOverflowAnchorEl)}
                onClose={() => setPageOverflowAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { p: 1, minWidth: 240, maxWidth: 360 } } }}
              >
                <Box
                  role="dialog"
                  aria-label={t(resolveTKey('table.moreActions'))}
                  sx={{ display: 'grid', gap: 0.75 }}
                >
                  {overflowPageActions.map(action => (
                    <ActionRenderer
                      key={action.action.id}
                      action={action}
                      size="small"
                      fullWidth
                      context={{ source: 'page' }}
                      onAction={(next, context) => {
                        setPageOverflowAnchorEl(null);
                        onPageAction(next, context);
                      }}
                    />
                  ))}
                </Box>
              </Popover>
            </>
          )}

          {hasViewOptions && (
            <Tooltip title={t(resolveTKey('table.viewOptions'))}>
              <IconButton
                size="small"
                onClick={() => setViewOptionsOpen(true)}
                disabled={loading}
                aria-label={t(resolveTKey('table.viewOptions'))}
                color={unsavedChanges ? 'warning' : 'default'}
              >
                <ViewOptionsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {actionNode}
        </Box>
      </Box>

      {hasActiveFilters && canShowActiveFilterSummary && (
        <Box
          sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}
        >
          {state.globalSearch && (
            <Chip
              size="small"
              variant="outlined"
              label={`${t(resolveTKey('table.globalSearch.placeholder'))}: ${state.globalSearch}`}
            />
          )}
          {activeFilterChips.map(filter => (
            <Chip key={filter.key} size="small" variant="outlined" label={filter.label} />
          ))}
          {canShowClearFilters && (
            <Button size="small" onClick={onClearFilters} disabled={loading}>
              {t(resolveTKey('table.clearFilters'))}
            </Button>
          )}
        </Box>
      )}

      <Dialog
        open={viewOptionsOpen}
        onClose={() => setViewOptionsOpen(false)}
        fullWidth
        maxWidth="sm"
        aria-labelledby="table-view-options-title"
      >
        <DialogTitle id="table-view-options-title">
          {t(resolveTKey('table.viewOptions'))}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            {canShowColumnChooser && (
              <Box>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
                  <ViewColumnIcon fontSize="small" />
                  <Typography variant="subtitle2">{t(resolveTKey('table.columns'))}</Typography>
                </Stack>
                <FormGroup>
                  {requiredColumns.map(column => (
                    <FormControlLabel
                      key={column.id}
                      control={<Checkbox size="small" checked disabled />}
                      label={t(resolveTKey(column.labelKey))}
                    />
                  ))}
                  {optionalColumns.map(column => {
                    const visible =
                      state.columnVisibility.find(item => item.columnId === column.id)?.visible !==
                      false;
                    return (
                      <FormControlLabel
                        key={column.id}
                        control={
                          <Checkbox
                            size="small"
                            checked={visible}
                            onChange={() => onColumnVisibilityChange(column.id, !visible)}
                          />
                        }
                        label={t(resolveTKey(column.labelKey))}
                      />
                    );
                  })}
                </FormGroup>
              </Box>
            )}

            {canShowDensity && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t(resolveTKey('table.densityLabel'))}
                </Typography>
                <RadioGroup
                  row={!isNarrow}
                  value={state.density}
                  onChange={event =>
                    onDensityChange(event.target.value as 'compact' | 'standard' | 'comfortable')
                  }
                >
                  <FormControlLabel
                    value="compact"
                    control={<Radio size="small" />}
                    label={t(resolveTKey('table.density.compact'))}
                  />
                  <FormControlLabel
                    value="standard"
                    control={<Radio size="small" />}
                    label={t(resolveTKey('table.density.standard'))}
                  />
                  <FormControlLabel
                    value="comfortable"
                    control={<Radio size="small" />}
                    label={t(resolveTKey('table.density.comfortable'))}
                  />
                </RadioGroup>
              </Box>
            )}

            {canShowGrouping && descriptor.columns.some(column => column.groupable) && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t(resolveTKey('table.groupBy'))}
                </Typography>
                <RadioGroup
                  value={state.grouping[0]?.field ?? ''}
                  onChange={event =>
                    onGroupingChange(
                      event.target.value ? [{ field: event.target.value, direction: 'asc' }] : []
                    )
                  }
                >
                  <FormControlLabel
                    value=""
                    control={<Radio size="small" />}
                    label={t(resolveTKey('common.none'))}
                  />
                  {descriptor.columns
                    .filter(column => column.groupable)
                    .map(column => (
                      <FormControlLabel
                        key={column.id}
                        value={column.field}
                        control={<Radio size="small" />}
                        label={t(resolveTKey(column.labelKey))}
                      />
                    ))}
                </RadioGroup>
                {descriptor.columns.some(column => column.allowedAggregations?.includes('sum')) && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <FormGroup>
                      {descriptor.columns
                        .filter(column => column.allowedAggregations?.includes('sum'))
                        .map(column => {
                          const selected = state.aggregations.some(
                            aggregation =>
                              aggregation.field === column.field && aggregation.function === 'sum'
                          );
                          return (
                            <FormControlLabel
                              key={column.id}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={selected}
                                  onChange={() =>
                                    onAggregationsChange(
                                      selected
                                        ? state.aggregations.filter(
                                            aggregation => aggregation.field !== column.field
                                          )
                                        : [
                                            ...state.aggregations,
                                            { field: column.field, function: 'sum' },
                                          ]
                                    )
                                  }
                                />
                              }
                              label={`${t(resolveTKey('table.sum'))} ${t(resolveTKey(column.labelKey))}`}
                            />
                          );
                        })}
                    </FormGroup>
                  </>
                )}
              </Box>
            )}

            {canShowSavedViews && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t(resolveTKey('table.savedViews'))}
                </Typography>
                <Stack spacing={1}>
                  {availableViews.map(view => (
                    <Box
                      key={view.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 1,
                        border: 1,
                        borderColor: view.id === currentViewId ? 'primary.main' : 'divider',
                        borderRadius: 1,
                      }}
                    >
                      <Button
                        size="small"
                        onClick={() => onSavedViewSelect(view.id)}
                        sx={{ flex: 1, justifyContent: 'flex-start' }}
                      >
                        {view.name}
                      </Button>
                      {!view.isDefault && (
                        <Button
                          size="small"
                          color="error"
                          onClick={() => onSavedViewDelete(view.id)}
                        >
                          {t('common:delete')}
                        </Button>
                      )}
                    </Box>
                  ))}
                  <TextField
                    size="small"
                    fullWidth
                    placeholder={t(resolveTKey('table.saveViewAs'))}
                    value={saveViewName}
                    onChange={event => setSaveViewName(event.target.value)}
                    onKeyDown={event => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        saveView();
                      }
                    }}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <SaveIcon fontSize="small" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <Button size="small" onClick={saveView} disabled={!saveViewName.trim()}>
                              {t('common:save')}
                            </Button>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </Stack>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {canShowRefresh && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<RefreshIcon fontSize="small" />}
                  onClick={onRefresh}
                  disabled={loading}
                >
                  {t(resolveTKey('table.refresh'))}
                </Button>
              )}
              {canShowResetLayout && (
                <Button size="small" variant="outlined" onClick={onResetLayout} disabled={loading}>
                  {t(resolveTKey('table.resetLayout'))}
                </Button>
              )}
              {canShowResetAll && (
                <Button size="small" variant="outlined" onClick={onResetAll} disabled={loading}>
                  {t(resolveTKey('table.resetAll'))}
                </Button>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewOptionsOpen(false)} variant="contained">
            {t('common:close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Use ExportExperience when descriptor has transfer.export capabilities,
          otherwise fall back to the legacy inline dialog. */}
      {descriptor.transfer?.export ? (
        <ExportExperience
          open={exportDialogOpen}
          onClose={() => setExportDialogOpen(false)}
          resourceLabel={
            descriptor.toolbar?.titleKey ? t(resolveTKey(descriptor.toolbar.titleKey)) : ''
          }
          contract={descriptor}
          tableState={state}
          selectedCount={selectedCount}
          filteredCount={totalCount}
          onExport={options => {
            if (onExport) onExport(options);
          }}
        />
      ) : (
        <Dialog
          open={exportDialogOpen}
          onClose={() => setExportDialogOpen(false)}
          fullWidth
          maxWidth="sm"
          data-testid="table-export-dialog"
        >
          <DialogTitle>{t(resolveTKey('table.exportLabel'))}</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t(resolveTKey('table.export.help'))}
            </Typography>
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t(resolveTKey('table.export.scope'))}
              </Typography>
              <RadioGroup
                value={exportScope}
                onChange={event =>
                  setExportScope(event.target.value as 'page' | 'selection' | 'all')
                }
                data-testid="table-export-scope"
              >
                {exportScopes.includes('page') && (
                  <FormControlLabel
                    value="page"
                    control={<Radio size="small" />}
                    label={t(resolveTKey('table.export.currentPage'))}
                  />
                )}
                {exportScopes.includes('selection') && (
                  <FormControlLabel
                    value="selection"
                    control={<Radio size="small" />}
                    label={t(resolveTKey('table.export.selectedRows'))}
                    disabled={!hasSelection}
                  />
                )}
                {exportScopes.includes('all') && (
                  <FormControlLabel
                    value="all"
                    control={<Radio size="small" />}
                    label={t(resolveTKey('table.export.allMatching'))}
                  />
                )}
              </RadioGroup>
            </FormControl>

            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t(resolveTKey('table.export.availableFields'))}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1, flexWrap: 'wrap' }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setExportFields([...visibleExportableFields])}
                disabled={visibleExportableFields.length === 0}
                data-testid="table-export-visible-fields"
              >
                {t(resolveTKey('table.export.visibleFields'))}
              </Button>
              <Button
                size="small"
                onClick={() => setExportFields(allFieldsSelected ? [] : [...allExportableFields])}
                disabled={allExportableFields.length === 0}
                data-testid="table-export-toggle-all"
              >
                {allFieldsSelected
                  ? t(resolveTKey('table.export.deselectAll'))
                  : t(resolveTKey('table.export.selectAll'))}
              </Button>
              <Typography variant="caption" color="text.secondary">
                {t(resolveTKey('table.export.fieldsSelected'), {
                  selected: exportFields.length,
                  total: allExportableColumns.length,
                })}
              </Typography>
            </Box>
            <FormGroup sx={{ maxHeight: 280, overflowY: 'auto' }}>
              {allExportableColumns.map(column => (
                <FormControlLabel
                  key={column.field}
                  control={
                    <Checkbox
                      size="small"
                      checked={exportFields.includes(column.field)}
                      onChange={event => toggleExportField(column.field, event.target.checked)}
                    />
                  }
                  label={t(resolveTKey(column.exportConfig?.exportLabelKey ?? column.labelKey))}
                />
              ))}
            </FormGroup>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setExportDialogOpen(false)} color="inherit">
              {t(resolveTKey('common.cancel'))}
            </Button>
            <Button
              onClick={() => {
                if (!onExport) return;
                onExport({ scope: exportScope, format: 'csv', fields: exportFields });
                setExportDialogOpen(false);
              }}
              variant="contained"
              startIcon={<ExportIcon fontSize="small" />}
              disabled={exportFields.length === 0}
              data-testid="table-export-submit"
            >
              {t(resolveTKey('table.exportLabel'))}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}
